import React, { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import { Stethoscope, HeartPulse, Scissors, FlaskConical, Sparkles, BedDouble, ArrowUpRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const categories = [
  {
    title: "Veterinary Consultation & Health Checkups",
    subtitle: "Professional Diagnosis",
    icon: <Stethoscope size={28} />,
    services: ["Expert Medical Checkup", "General Health Exams", "Treatment Planning", "Accurate Diagnosis"],
    path: "/expert-pet-care",
    accent: "from-emerald-400 to-primary",
    glow: "bg-primary/15",
    badgeColor: "bg-primary/10 text-primary",
    number: "01"
  },
  {
    title: "Pet Vaccination & Preventive Care",
    subtitle: "Immunity Protocols",
    icon: <HeartPulse size={28} />,
    services: ["Regular Vaccination", "Preventive Programs", "Deworming", "Health Maintenance"],
    path: "/expert-pet-care",
    accent: "from-red-400 to-rose-600",
    glow: "bg-red-500/15",
    badgeColor: "bg-red-500/10 text-red-600",
    number: "02"
  },
  {
    title: "Advanced Pet Surgeries",
    subtitle: "Surgical Excellence",
    icon: <Scissors size={28} />,
    services: ["Sterilization (Spaying)", "Tumor Surgeries", "Bone & Joint", "Emergency Operations"],
    path: "/surgeries",
    accent: "from-amber-400 to-orange-500",
    glow: "bg-amber-500/15",
    badgeColor: "bg-amber-500/10 text-amber-600",
    number: "03"
  },
  {
    title: "Emergency & Critical Care",
    subtitle: "24/7 Urgent Response",
    icon: <FlaskConical size={28} />,
    services: ["Injuries & Accidents", "Sudden Illness", "Breathing Issues", "Toxic Exposure"],
    path: "/critical-care",
    accent: "from-blue-400 to-indigo-600",
    glow: "bg-blue-500/15",
    badgeColor: "bg-blue-500/10 text-blue-600",
    number: "04"
  },
  {
    title: "Diagnostic Services",
    subtitle: "Advanced Testing Lab",
    icon: <Sparkles size={28} />,
    services: ["Blood Analysis", "X-Ray Imaging", "Ultrasound Scanning", "Early Detection"],
    path: "/diagnostic-services",
    accent: "from-violet-400 to-purple-600",
    glow: "bg-violet-500/15",
    badgeColor: "bg-violet-500/10 text-violet-600",
    number: "05"
  },
  {
    title: "Pet Grooming & Hygiene Care",
    subtitle: "Grooming Solutions",
    icon: <BedDouble size={28} />,
    services: ["Cleaning & Bathing", "Nail Trimming", "Coat Maintenance", "Hygiene Control"],
    path: "/grooming",
    accent: "from-teal-400 to-cyan-600",
    glow: "bg-teal-500/15",
    badgeColor: "bg-teal-500/10 text-teal-600",
    number: "06"
  }
];

const Services = () => {
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.svc-card',
        { y: 60, opacity: 0, scale: 0.96 },
        {
          y: 0, opacity: 1, scale: 1,
          stagger: 0.12, duration: 0.9, ease: 'power3.out',
          scrollTrigger: { trigger: containerRef.current, start: 'top 78%' }
        }
      );
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={containerRef} className="py-24 md:py-32 bg-white relative overflow-hidden" id="services">
      {/* Section background glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/4 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-blue-500/4 rounded-full blur-[150px] pointer-events-none" />

      <div className="section-container relative z-10">

        {/* ── Section Header ── */}
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-primary/30" />
            <span className="text-primary font-black tracking-[0.25em] uppercase text-[10px]">Clinical Specialities</span>
            <span className="w-10 h-px bg-primary/30" />
          </div>
          <h2 className="text-navy font-display font-bold leading-[0.92] tracking-tight text-[clamp(2.5rem,7vw,5rem)] mb-5">
            Comprehensive <em className="font-normal text-primary not-italic">Veterinary Care.</em>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg leading-relaxed">
            Combining diagnostic precision with compassionate surgical excellence to ensure your pets receive world-class medical attention.
          </p>
        </div>

        {/* ── Cards Grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((cat, i) => (
            <Link to={cat.path} key={i} className="svc-card group relative bg-white border border-navy/8 rounded-[2.5rem] overflow-hidden hover:shadow-[0_30px_80px_rgba(0,0,20,0.12)] hover:-translate-y-3 transition-all duration-700 flex flex-col">

              {/* ── Top gradient bar ── */}
              <div className={`h-1.5 w-full bg-gradient-to-r ${cat.accent} opacity-80 group-hover:opacity-100 transition-opacity`} />

              {/* ── Glow chip (atmospheric) ── */}
              <div className={`absolute -top-12 -right-12 w-48 h-48 ${cat.glow} rounded-full blur-[70px] group-hover:opacity-100 opacity-60 transition-opacity duration-700`} />

              <div className="relative z-10 p-8 md:p-10 flex flex-col flex-1">

                {/* ── Card Top Row ── */}
                <div className="flex items-start justify-between mb-8">
                  {/* Icon container */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${cat.accent} flex items-center justify-center text-white shadow-lg group-hover:scale-110 group-hover:-rotate-3 transition-all duration-500`}>
                    {cat.icon}
                  </div>
                  {/* Number badge */}
                  <span className="text-[11px] font-black tracking-widest text-navy/20 group-hover:text-primary/40 transition-colors duration-500 tabular-nums">{cat.number}</span>
                </div>

                {/* ── Title ── */}
                <div className="mb-6">
                  <h3 className="font-bold text-navy text-[1.4rem] leading-tight mb-1 group-hover:text-primary transition-colors duration-500">{cat.title}</h3>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground/70">{cat.subtitle}</p>
                </div>

                {/* ── Service Pills ── */}
                <div className="flex flex-wrap gap-2 mb-8 flex-1">
                  {cat.services.map((item, j) => (
                    <span key={j} className={`text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-full border transition-all duration-500 ${cat.badgeColor} border-current/20 group-hover:scale-[1.02]`}>
                      {item}
                    </span>
                  ))}
                </div>

                {/* ── Footer CTA ── */}
                <div className="mt-auto pt-6 border-t border-navy/6 flex items-center justify-between">
                  <div>
                    <span className="block text-[9px] font-black uppercase tracking-[0.25em] text-primary/50 mb-0.5">Catalogue</span>
                    <span className="block font-bold text-navy text-sm group-hover:text-primary transition-colors duration-500">View Specifications</span>
                  </div>
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${cat.accent} flex items-center justify-center text-white shadow-md group-hover:shadow-xl group-hover:scale-110 transition-all duration-500`}>
                    <ArrowUpRight size={18} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
